/* eslint-disable react/no-unstable-nested-components -- DataTable `cell` callbacks are called as plain functions, never mounted as components. */
import { ScanFace } from 'lucide-react';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { hubUrl } from '@/nav/hubs';
import { Checkbox } from '@/components/ui/checkbox';
import { api, type DossierStatus } from '../../api';
import { DOSSIER_VIEWS, type DossierType, type DossierView } from '../../api/areas/accounts';
import { DataTable, FilterBar, Section, type Column } from '../../components/blocks';
import { DataError } from '../../components/DataError';
import { EntityRef } from '../../components/EntityRef';
import { PageHeader, useDocumentTitle } from '../../components/PageHeader';
import { EmptyState, Loading } from '../../components/States';
import { formatCount, formatDate, formatDateTime, formatRelative } from '../../lib/format';
import { useApi } from '../../lib/useApi';
import {
  DOSSIER_STATUS,
  EnumBadge,
  EnumText,
  FilterChip,
  FilterSelect,
  FilterText,
  mutedSmall,
  Pager,
  paths,
  RefreshButton,
  ResetFilters,
  useEnumLabel,
  useListParams,
  useRecordName,
} from './shared';

const LIMIT = 50;
export const DOSSIER_TYPES: readonly DossierType[] = ['VN_DOMAIN', 'INTERNATIONAL_GTLD', 'INTERNATIONAL_NEW_GTLD', 'INTERNATIONAL_CCTLD', 'OTHER'];

type Row = Awaited<ReturnType<typeof api.listDossiers>>['items'][number];

/** `/dossiers`: domain dossiers, current versions by default. */
export function DossiersPage() {
  const { t } = useTranslation('accounts');
  const label = useEnumLabel('accounts');
  useDocumentTitle(t('dossiers.title'));
  const id = useId();
  const list = useListParams(['domain', 'status', 'dossier_type', 'subject_id', 'superseded', 'view'] as const);
  const { domain, status, dossier_type, subject_id, superseded, view } = list.values;
  const result = useApi(
    (signal) =>
      api.listDossiers(
        {
          domain: domain || undefined,
          status: (status || undefined) as DossierStatus | undefined,
          dossier_type: (dossier_type || undefined) as DossierType | undefined,
          subject_id: subject_id || undefined,
          include_superseded: superseded === '1' || undefined,
          view: (DOSSIER_VIEWS as readonly string[]).includes(view) ? (view as DossierView) : undefined,
          after: list.after || undefined,
          limit: LIMIT,
        },
        { signal },
      ),
    [domain, status, dossier_type, subject_id, superseded, view, list.after],
  );

  const firstRow = result.status === 'success' ? result.data.items[0] : undefined;
  const subjectName = useRecordName('subject', subject_id, firstRow?.subject_id === subject_id ? firstRow.subject_legal_name : null);

  const columns: Column<Row>[] = [
    {
      key: 'domain',
      header: t('dossiers.col.domain'),
      cell: (x) => <EntityRef to={paths.dossier(x.id)} id={x.id} label={x.unicode_name} sub={x.unicode_name !== x.ascii_name ? <span className="font-mono">{x.ascii_name}</span> : undefined} className="min-w-36" />,
    },
    { key: 'type', header: t('dossiers.col.type'), className: 'min-w-28 whitespace-normal', cell: (x) => <EnumText group="dossierType" value={x.dossier_type} /> },
    {
      key: 'status',
      header: t('dossiers.col.status'),
      cell: (x) => (
        <>
          <EnumBadge group="dossierStatus" tones={DOSSIER_STATUS} value={x.status} />
          <span className={`block ${mutedSmall}`}>{t('common.versionN', { n: x.dossier_version })}</span>
        </>
      ),
    },
    {
      key: 'registrant',
      header: t('dossiers.col.registrant'),
      className: 'whitespace-normal',
      cell: (x) =>
        x.subject_id ? (
          <EntityRef to={paths.subject(x.subject_id)} id={x.subject_id} label={x.subject_legal_name ?? t('common.hiddenName')} className="min-w-32" />
        ) : (
          <span className="text-muted-foreground">{t('dossiers.notBound')}</span>
        ),
    },
    { key: 'customer', header: t('dossiers.col.customer'), className: 'whitespace-normal', cell: (x) => <EntityRef to={paths.customer(x.customer_id)} id={x.customer_id} label={x.customer_name ?? undefined} className="min-w-32" /> },
    { key: 'documents', header: t('dossiers.col.documents'), numeric: true, cell: (x) => formatCount(x.document_count) },
    {
      key: 'vnnic',
      header: t('dossiers.col.vnnic'),
      className: 'whitespace-normal',
      cell: (x) =>
        x.dossier_type === 'VN_DOMAIN' ? (
          <span className={mutedSmall}>
            {[x.registrar_signed === undefined ? null : x.registrar_signed ? t('dossiers.signed') : t('dossiers.notSigned'), x.verification_method === 'EKYC' ? 'eKYC' : null, x.vnnic_sync_status === undefined ? null : x.vnnic_sync_status === 'SUBMITTED' || x.vnnic_sync_status === 'ACKNOWLEDGED' ? t('dossiers.onVnnic') : t('dossiers.notSynced')]
              .filter(Boolean)
              .join(' · ')}
          </span>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    { key: 'completed', header: t('dossiers.col.completed'), className: 'whitespace-nowrap', cell: (x) => formatDate(x.completed_at) },
    {
      key: 'updated',
      header: t('dossiers.col.updated'),
      className: 'whitespace-nowrap',
      cell: (x) => (
        <span className={mutedSmall} title={formatDateTime(x.updated_at)}>
          {formatRelative(x.updated_at)}
        </span>
      ),
    },
  ];

  return (
    <>
      <PageHeader title={t('dossiers.title')} crumbs={[{ label: t('crumb') }]} subtitle={t('dossiers.subtitle')} actions={<RefreshButton onClick={result.reload} />} />
      {/* The HostBill tabs as views of this list. */}
      <nav aria-label={t('dossiers.views')} className="flex flex-wrap items-center gap-1" data-dossier-views>
        {(['', ...DOSSIER_VIEWS] as const).map((v) => (
          <button
            key={v || 'all'}
            type="button"
            aria-pressed={view === v}
            onClick={() => list.setFilter('view', v)}
            className={cn('h-8 rounded-md border px-3 text-sm', view === v ? 'border-primary bg-primary/10 font-medium text-primary' : 'bg-background hover:bg-muted')}
            data-dossier-view={v || 'ALL'}
          >
            {t(`dossiers.view.${v || 'ALL'}`)}
          </button>
        ))}
        <Button asChild size="sm" variant="outline" className="ml-auto">
          <Link to={hubUrl('home', 'dossiers', undefined, { method: 'EKYC', stage: 'AWAITING_CUSTOMER' })} title={t('dossiers.createEkycHint')}>
            <ScanFace /> {t('dossiers.createEkyc')}
          </Link>
        </Button>
      </nav>
      <FilterBar actions={<ResetFilters show={list.filtered} onReset={list.reset} />}>
        <FilterText id={`${id}-d`} label={t('dossiers.domainFilter')} value={domain} placeholder={t('dossiers.domainPlaceholder')} onApply={(v) => list.setFilter('domain', v.toLowerCase())} />
        <FilterSelect id={`${id}-s`} label={t('filter.status')} value={status} options={Object.keys(DOSSIER_STATUS).map((s) => [s, label('dossierStatus', s)] as const)} onChange={(v) => list.setFilter('status', v)} />
        <FilterSelect
          id={`${id}-t`}
          label={t('filter.type')}
          value={dossier_type}
          className="sm:w-52"
          options={DOSSIER_TYPES.map((s) => [s, label('dossierType', s)] as const)}
          onChange={(v) => list.setFilter('dossier_type', v)}
        />
        <label className="flex h-8 items-center gap-2 text-sm" htmlFor={`${id}-sup`}>
          <Checkbox id={`${id}-sup`} checked={superseded === '1'} onCheckedChange={(c) => list.setFilter('superseded', c === true ? '1' : '')} />
          <span>{t('dossiers.includeSuperseded')}</span>
        </label>
        {subject_id ? <FilterChip label={t('filter.subject', { name: subjectName })} title={subject_id} onClear={() => list.setFilter('subject_id', '')} /> : null}
      </FilterBar>
      <Section flush>
        {result.status === 'loading' ? (
          <div className="px-5">
            <Loading rows={5} />
          </div>
        ) : null}
        {result.status === 'error' ? (
          <div className="px-5 pb-5">
            <DataError error={result.error} onRetry={result.reload} title={t('dossiers.loadError')} />
          </div>
        ) : null}
        {result.status === 'success' ? (
          <>
            <DataTable columns={columns} rows={result.data.items} rowKey={(x) => x.id} empty={<EmptyState title={t('dossiers.empty')} />} />
            {result.data.items.length > 0 ? <Pager shown={result.data.items.length} hasPrev={Boolean(list.after)} nextAfter={result.data.next_after} onPrev={list.prev} onNext={list.next} /> : null}
          </>
        ) : null}
      </Section>
    </>
  );
}
